import {
  getDefaultBreak,
  getOpenPeriods,
  isBreakWithinHours,
  type MinuteRange,
} from "@shared/hoursOfOperation";
import { parseStoredTimeOfDay } from "@shared/timeOfDay";
import { timeOfDayISO, toCanonicalTimeOfDay } from "./timeOfDay";

export interface TimeOfDayRange {
  start: string;
  end: string;
}

type MaybeTimeOfDayRange =
  | { start?: string | null; end?: string | null }
  | null
  | undefined;

export const HOURS_OF_OPERATION_DAYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;

export type HoursOfOperationDay = (typeof HOURS_OF_OPERATION_DAYS)[number];

export const isHoursOfOperationDay = (
  key: string,
): key is HoursOfOperationDay =>
  (HOURS_OF_OPERATION_DAYS as readonly string[]).includes(key);

/**
 * Whether an hours object has any day keys at all. Its other keys (`breaks`,
 * `__typename`) don't count.
 */
export const hasHoursOfOperationDays = (data?: object | null) =>
  !!data && Object.keys(data).some(isHoursOfOperationDay);

/** Minutes since midnight for a stored time of day, in either encoding. */
export const timeOfDayToMinutes = (stored?: string | null): number | null => {
  if (!parseStoredTimeOfDay(stored)) return null;
  const instant = new Date(toCanonicalTimeOfDay(stored as string));
  if (Number.isNaN(instant.getTime())) return null;
  return instant.getUTCHours() * 60 + instant.getUTCMinutes();
};

const minutesToTimeOfDayISO = (minutes: number) =>
  timeOfDayISO(Math.floor(minutes / 60) % 24, minutes % 60);

const toMinuteRange = (range: MaybeTimeOfDayRange): MinuteRange | null => {
  const start = timeOfDayToMinutes(range?.start);
  const end = timeOfDayToMinutes(range?.end);
  return start === null || end === null ? null : { start, end };
};

const toTimeOfDayRange = (range: MinuteRange): TimeOfDayRange => ({
  start: minutesToTimeOfDayISO(range.start),
  end: minutesToTimeOfDayISO(range.end),
});

/**
 * The periods a business is actually open on a day, as canonical ISO times of
 * day. Without breaks this is just the day's own range.
 */
export const getOpenPeriodsTimeOfDay = (
  day: MaybeTimeOfDayRange,
  breaks?: MaybeTimeOfDayRange[] | null,
): TimeOfDayRange[] => {
  const hours = toMinuteRange(day);
  if (!hours) return [];
  const dayBreaks = (breaks ?? [])
    .map(toMinuteRange)
    .filter((range): range is MinuteRange => range !== null);
  return getOpenPeriods(hours, dayBreaks).map(toTimeOfDayRange);
};

export const isTimeOfDayBreakWithinHours = (
  day: MaybeTimeOfDayRange,
  dayBreak: MaybeTimeOfDayRange,
): boolean => {
  const hours = toMinuteRange(day);
  const range = toMinuteRange(dayBreak);
  return !!hours && !!range && isBreakWithinHours(hours, range);
};

export const getDefaultTimeOfDayBreak = (
  day: MaybeTimeOfDayRange,
): TimeOfDayRange | null => {
  const hours = toMinuteRange(day);
  const dayBreak = hours ? getDefaultBreak(hours) : null;
  return dayBreak ? toTimeOfDayRange(dayBreak) : null;
};
